import Box from '~/common/components/Box';
import SimpleGrid from '~/common/components/Grids/Grid/SimpleGrid';
import Text from '~/common/components/Texts/Text';
import { formatCurrency } from '~/common/util/format';
import { FormTypes } from '~/pages/IncomeCalculator/enums';
import type { CalculationResult } from '~/pages/IncomeCalculator/types';

import BreakdownContainer from '../BreakdownContainer';
import BreakdownValue from '../styles/BreakdownValue';
import BreakdownYearContainer from '../styles/BreakdownYearContainer';

const formType = FormTypes.ScheduleC;

type CalculatedMileageProps = {
  year: number;
  selectedResult: CalculationResult;
};

const CalculatedMileage = ({
  year,
  selectedResult,
}: Readonly<CalculatedMileageProps>) => {
  const milesDriven = selectedResult.getInput(
    formType,
    year,
    'milesDrivenForBusiness',
  )?.fieldValue;

  const taxMileageRate = selectedResult.getInputAsFloat(
    formType,
    year,
    'taxMileageRate',
  );

  return (
    <Box pb={4}>
      <Text fontWeight="bold">Calculated Vehicle Miles ({year})</Text>
      <Text fontSize="sm">Vehicle Miles * Tax Mileage Rate</Text>
      <BreakdownValue name="Vehicle Miles:" value={milesDriven} />
      <BreakdownValue
        name="Tax Mileage Rate:"
        value={taxMileageRate}
        isCurrency={true}
      />
      <Text fontSize="sm">
        {milesDriven} * {taxMileageRate}
      </Text>
    </Box>
  );
};

type AnnualIncomeProps = {
  year: number;
  selectedResult: CalculationResult;
};

const AnnualIncome = ({
  year,
  selectedResult,
}: Readonly<AnnualIncomeProps>) => {
  return (
    <Box>
      <Text fontWeight="bold">Annual Income ({year})</Text>

      <BreakdownValue
        name="Net Profit/Loss"
        value={selectedResult.getInput(formType, year, 'netProfit')?.fieldValue}
        testId={`net-profit-${year}`}
        isCurrency={true}
      />
      <BreakdownValue
        name="+ Depreciation"
        value={
          selectedResult.getInput(formType, year, 'depreciation')?.fieldValue
        }
        testId={`depreciation-${year}`}
        isCurrency={true}
      />
      <BreakdownValue
        name="+ Depletion"
        value={selectedResult.getInput(formType, year, 'depletion')?.fieldValue}
        testId={`depletion-${year}`}
        isCurrency={true}
      />
      <BreakdownValue
        name="+ Business Use of Home"
        value={selectedResult.getInput(formType, year, 'useOfHome')?.fieldValue}
        testId={`use-home-${year}`}
        isCurrency={true}
      />
      <BreakdownValue
        name="+ Business Use of Home (Form 8829)"
        value={
          selectedResult.getInput(formType, year, 'calculatedBusinessUseOfHome')
            ?.fieldValue
        }
        testId={`use-home-${year}`}
        isCurrency={true}
      />
      <BreakdownValue
        name="+ Calculated Vehicle Miles"
        value={
          selectedResult.getInput(formType, year, 'calculatedVehicleMiles')
            ?.fieldValue
        }
        testId={`calc-miles-${year}`}
        isCurrency={true}
      />
      <BreakdownValue
        name="- Meals and Entertainment"
        value={
          selectedResult.getInput(formType, year, 'mealsAndEntertainment')
            ?.fieldValue
        }
        testId={`meals-${year}`}
        isCurrency={true}
      />
      <BreakdownValue
        name="- Non-Recurring Other Income"
        value={
          selectedResult.getInput(formType, year, 'otherIncome')?.fieldValue
        }
        testId={`other-income-${year}`}
        isCurrency={true}
      />
    </Box>
  );
};

type ScheduleCCalculationBreakdownProps = {
  selectedResult: CalculationResult;
  onNewWindow?: (...args: any[]) => any;
  hideAccordion?: boolean;
};

const ScheduleCCalculationBreakdown = ({
  selectedResult,
  onNewWindow,
  hideAccordion = false,
}: Readonly<ScheduleCCalculationBreakdownProps>) => {
  const years = selectedResult.getFiscalYears().sort((a, b) => b - a);

  const OneYearBreakdown = () => {
    const year = years[0];
    return (
      <>
        <SimpleGrid
          w={`${50 * Math.min(years.length, 2)}%`}
          columns={Math.min(2, years.length)}
          spacingX={0}
          spacingY={8}
          pb={4}
        >
          <BreakdownYearContainer title={year}>
            <CalculatedMileage year={year} selectedResult={selectedResult} />

            <AnnualIncome year={year} selectedResult={selectedResult} />
          </BreakdownYearContainer>
        </SimpleGrid>
        <Text fontWeight="bold">Calculated Monthly Income/Loss:</Text>
        <Text fontSize="sm" pb={2} borderBottom="1px solid #005F9E">
          {formatCurrency(
            selectedResult.getInput(formType, year, 'annualIncome')?.fieldValue,
          )}{' '}
          / 12
        </Text>

        <BreakdownValue
          name="Net Income"
          value={selectedResult.calculationAmount}
          isCurrency={true}
          pt={2}
          fontWeight="bold"
        />
      </>
    );
  };

  const TwoYearBreakdown = () => {
    const currentYear = years[0];
    const priorYear = years[1];

    const currentAnnualIncome = Number.parseFloat(
      selectedResult.getInput(formType, currentYear, 'annualIncome')
        ?.fieldValue,
    );

    const priorAnnualIncome = Number.parseFloat(
      selectedResult.getInput(formType, priorYear, 'annualIncome')?.fieldValue,
    );

    return (
      <>
        <SimpleGrid
          w={`${50 * Math.min(years.length, 2)}%`}
          columns={Math.min(2, years.length)}
          spacing={8}
          pb={4}
        >
          <BreakdownYearContainer title={currentYear}>
            <CalculatedMileage
              year={currentYear}
              selectedResult={selectedResult}
            />
            {
              <AnnualIncome
                year={currentYear}
                selectedResult={selectedResult}
              />
            }
          </BreakdownYearContainer>
          <BreakdownYearContainer title={priorYear}>
            <CalculatedMileage
              year={priorYear}
              selectedResult={selectedResult}
            />

            {<AnnualIncome year={priorYear} selectedResult={selectedResult} />}
          </BreakdownYearContainer>
        </SimpleGrid>
        <Text fontWeight="bold">Calculated Monthly Income/Loss:</Text>
        {currentAnnualIncome > priorAnnualIncome ? (
          <Text fontSize="sm" pb={2} borderBottom="1px solid #005F9E">
            {formatCurrency(currentAnnualIncome)}
            {' + '}
            {formatCurrency(priorAnnualIncome)}
            {' / 24 '}
          </Text>
        ) : (
          <Text fontSize="sm" pb={2} borderBottom="1px solid #005F9E">
            {formatCurrency(currentAnnualIncome)} / 12
          </Text>
        )}

        <BreakdownValue
          name="Net Income"
          value={selectedResult.calculationAmount}
          isCurrency={true}
          pt={2}
          fontWeight="bold"
        />
      </>
    );
  };

  return (
    <BreakdownContainer
      header="Schedule C Breakdown"
      onNewWindow={onNewWindow}
      hideAccordion={hideAccordion}
    >
      {years.length === 1 && <OneYearBreakdown />}
      {years.length >= 2 && <TwoYearBreakdown />}
    </BreakdownContainer>
  );
};

export default ScheduleCCalculationBreakdown;
